import { useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Home from "../components/home/Home";
import { useHomeData } from "../hooks/useHomeData";
import { useHomeSubscription } from "../hooks/useHomeSubscription";
import { useWebOtpListener } from "../hooks/useWebOtpListener";
import { NavigateToLanding, getDataFromCookies } from "../utils/CommonFunction";
import tagManagerEvents from "../utils/GoogleTagManager";
import type { MostViewedVideo } from "../types/home";

const PAGE_TITLE = "Sekho";

export interface HomeScreenProps {
  token: string | null;
  loaderStatus: (status: boolean) => void;
  /** Closes the lucky-draw popup (owned by the app shell). */
  handleClose: () => void;
  /** Lucky-draw popup visibility. */
  show: boolean;
}

/**
 * Business-logic container for the Home screen. Owns data fetching, the
 * subscription flow, analytics and navigation, then renders the pure `Home` UI.
 */
const HomeScreen = ({
  token,
  loaderStatus,
  handleClose,
  show,
}: HomeScreenProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const utmSource = searchParams.get("utm_source") ?? "";

  const { cookieMsisdn } = getDataFromCookies();
  const isSubscribed = typeof window !== "undefined" && Boolean(cookieMsisdn);

  const webOtp = useWebOtpListener();
  const { categories, courses, mostViewedVideos } = useHomeData(
    token,
    loaderStatus,
  );
  const subscription = useHomeSubscription(token, utmSource);

  useEffect(() => {
    tagManagerEvents("Home_page", utmSource);
    document.title = PAGE_TITLE;
  }, [utmSource]);

  const handleMostViewedClick = useCallback(
    (video: MostViewedVideo) => {
      tagManagerEvents("Most_viewed_videos", utmSource);
      if (isSubscribed) {
        navigate(
          `/course_detail?courseId=${video.course.id}&videoId=${video.id}`,
        );
      } else {
        NavigateToLanding();
      }
    },
    [isSubscribed, navigate, utmSource],
  );

  return (
    <Home
      show={show}
      onCloseModal={handleClose}
      categories={categories}
      courses={courses}
      mostViewedVideos={mostViewedVideos}
      isSubscribed={isSubscribed}
      token={token}
      utmSource={utmSource}
      webOtp={webOtp}
      otpStatus={subscription.otpStatus}
      loader={subscription.loader}
      error={subscription.error}
      phNumber={subscription.phNumber}
      onSubscribe={subscription.subscribe}
      onVerifyOtp={subscription.verifyOtp}
      setUserPackage={subscription.setUserPackage}
      setLoader={subscription.setLoader}
      setIsActive={subscription.setIsActive}
      onMostViewedClick={handleMostViewedClick}
    />
  );
};

export default HomeScreen;
